import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import Quiz from './Quiz';

function QuizMode({ userId }) {
  const [selectedLevel, setSelectedLevel] = useState(null);
  const [wordCount, setWordCount] = useState(0);
  const [wordsWithDistractors, setWordsWithDistractors] = useState(0);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const levels = [
    {
      id: 1,
      name: 'Multiple Choice',
      description: 'Test your vocabulary with multiple choice questions',
      unlocked: true,
    }
  ];

  const handleLevelSelect = (level) => {
    if (level.unlocked) setSelectedLevel(level);
  };

  const handleBackToLevels = () => setSelectedLevel(null);
  const handleBackToWordInput = () => navigate('/word-input');

  useEffect(() => {
    const checkWords = async () => {
      if (!userId) { setLoading(false); return; }
      try {
        const { data: allWords, error } = await supabase
          .from('words')
          .select('*')
          .eq('user_id', userId);
        if (error) return;
        setWordCount(allWords.length);
        setWordsWithDistractors(allWords.filter(w => w.distractors && w.distractors.length >= 3).length);
      } catch (err) {}
      finally {
        setLoading(false);
      }
    };
    checkWords();
  }, [userId]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="w-8 h-8 border-4 border-gray-200 border-t-lattice-green rounded-full animate-spin" />
        <p className="text-lattice-muted text-sm">Loading...</p>
      </div>
    );
  }

  if (selectedLevel) {
    return <Quiz userId={userId} onBack={handleBackToLevels} level={selectedLevel} />;
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-2xl font-bold text-lattice-dark">Quiz Mode</h2>
        <button
          onClick={handleBackToWordInput}
          className="text-sm text-lattice-muted hover:text-lattice-dark transition-colors"
        >
          Back to Word Input
        </button>
      </div>

      {/* Hero card */}
      <div className="p-6 bg-lattice-bg border border-lattice-card-border rounded-xl mb-8">
        <h3 className="text-lattice-dark text-lg font-semibold mb-1">Ready to test your vocabulary?</h3>
        <p className="text-lattice-muted text-sm leading-relaxed">
          Challenge yourself with multiple choice questions to see how well you know your words.
        </p>
      </div>

      {/* Level cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {levels.map((level) => (
          <div
            key={level.id}
            onClick={() => handleLevelSelect(level)}
            className={`p-6 border rounded-xl transition-all ${
              level.unlocked
                ? 'bg-white border-lattice-card-border cursor-pointer hover:border-lattice-green hover:shadow-sm'
                : 'bg-gray-50 border-gray-100 opacity-60 cursor-default'
            }`}
          >
            <div className="flex justify-between items-start mb-3">
              <h4 className={`text-base font-semibold ${level.unlocked ? 'text-lattice-dark' : 'text-gray-400'}`}>
                Level {level.id}: {level.name}
              </h4>
              {!level.unlocked && <span className="text-gray-400 text-sm">Locked</span>}
            </div>

            <p className={`text-sm leading-relaxed mb-4 ${level.unlocked ? 'text-lattice-muted' : 'text-gray-400'}`}>
              {level.description}
            </p>

            {level.unlocked && (
              <span className="inline-block text-xs font-medium px-3 py-1 bg-green-50 text-lattice-green border border-green-200 rounded-full">
                Available
              </span>
            )}
          </div>
        ))}
      </div>

      {/* Word collection stats */}
      <div className="p-5 bg-lattice-bg border border-lattice-card-border rounded-xl mb-4">
        <h4 className="text-lattice-dark font-semibold text-sm mb-4">Your word collection</h4>
        <div className="flex gap-8">
          <div>
            <div className="text-2xl font-bold text-lattice-dark">{wordCount}</div>
            <div className="text-xs text-lattice-muted mt-0.5">Total words</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-lattice-green">{wordsWithDistractors}</div>
            <div className="text-xs text-lattice-muted mt-0.5">Quiz ready</div>
          </div>
        </div>
        {wordsWithDistractors === 0 && wordCount > 0 && (
          <p className="text-lattice-muted text-xs mt-3 italic">
            Your words don't have distractors yet. Add words with meanings to generate them automatically.
          </p>
        )}
      </div>

      {/* Quick start */}
      <div className="p-5 bg-white border border-lattice-card-border rounded-xl">
        <h4 className="text-lattice-dark font-semibold text-sm mb-1">Getting started</h4>
        <p className="text-lattice-muted text-sm leading-relaxed mb-4">
          Add some words with their meanings. AI will generate distractors automatically, then you can quiz yourself here.
        </p>
        <button
          onClick={handleBackToWordInput}
          className="px-5 py-2 bg-lattice-green hover:bg-lattice-green-hover text-lattice-btn-text rounded-lg text-sm font-medium transition-colors"
        >
          Add your first word
        </button>
      </div>
    </div>
  );
}

export default QuizMode;
